import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map, retry } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ScanFile } from '../models/file.model';
import { OrderList, PayOrderRequest } from '../models/order.model';
import { exclusiveStartKey, Scan, scansList } from '../models/scan.model';

const BASE_URL = environment.base_url;
const BASE_URL_SECOND = environment.base_url_second;

export interface NewScanResponse {
    ID: string,
    awsUrl: string,
    signedZipUrl: string;
}

@Injectable({
    providedIn: 'root'
})
export class ScanService {

    public exclusiveStartKey: exclusiveStartKey;

    constructor(
        private http: HttpClient
    ) { }

    // Create a function to generate common headers
    private getCommonHeaders(): { [key: string]: string } {
        const token = localStorage.getItem('token') || '';
        return {
            'Authorization': `Bearer ${token}`,
            'Accept-Encoding': 'gzip, deflate, br'
        };
    }


    getAllScans(): Observable<scansList> {

        return this.http.get(`${BASE_URL}/scans`, {
            headers: this.getCommonHeaders(),
            // @ts-ignore
            params: this.exclusiveStartKey ? {
                ID: this.exclusiveStartKey.ID,
                userId: this.exclusiveStartKey.userId,
                date: this.exclusiveStartKey.date
            } : null
        }).pipe(
            // @ts-ignore
            map((resp: {
                scans: Scan[],
                exclusiveStartKey?: exclusiveStartKey
            }) => {
                if (resp.exclusiveStartKey) {
                    this.exclusiveStartKey = resp.exclusiveStartKey;
                } else {
                    // @ts-ignore
                    this.exclusiveStartKey = null;
                }

                return {
                    scans: resp.scans,
                    hasMore: this.exclusiveStartKey ? true : false
                };
            })
        );
    }

    getOne(scanId: string): Observable<Scan> {
        return this.http.get<Scan>(`${BASE_URL}/scan/${scanId}`, {
            headers: this.getCommonHeaders()
        }).pipe(
            map((resp: Scan) => resp)
        );
    }


    getSignedZipUrl(orderId: string): Observable<any> {
        return this.http.get(`${BASE_URL}/scan/signedZipUrl/${orderId}`, {
            headers: this.getCommonHeaders()
        }).pipe(
            map((resp) => resp)
        );
    }



    getFiles(scanId: string): Observable<ScanFile[]> {

        return this.http.get<{ filesFound: ScanFile[] }>(`${BASE_URL}/files/${scanId}`, {
            headers: this.getCommonHeaders()
        }).pipe(
            map((resp: { filesFound: ScanFile[] }) => resp.filesFound)
        );
    }

    getFile(scanId: string, fileName: string): Observable<any> {
        return this.http.get(`${BASE_URL}/file/${scanId}/${fileName}`, {
            headers: this.getCommonHeaders()
        });
    }

    uploadSignedUri(scanId: string, fileName: string): Observable<any> {
        return this.http.get(`${BASE_URL}/uploadSignerUri/${scanId}/${fileName}`, {
            headers: this.getCommonHeaders()
        }).pipe(
            retry(1)
        );
    }

    uploadZipFile(uri: string, zip: Blob): Observable<any> {
        return this.http.put(uri, zip, {
            headers: new HttpHeaders({ 'Content-Type': 'application/zip' }),
            responseType: 'text' as 'json',  // evita parseo JSON
            observe: 'response'              // devuelve HttpResponse completo
        });
    }

    uploadFileToSignedUriWithPogress(uri: string, formData: any): Observable<HttpEvent<any>> {
        let headers = new HttpHeaders();

        return this.http.put(uri, formData, {
            headers,
            reportProgress: true,
            observe: 'events' // necesario para ver los eventos de progreso
        }).pipe(retry(2));
    }


    syncS3fileToScan(scanId: string): Observable<any> {
        return this.http.get(`${BASE_URL}/fileSync/${scanId}`, {
            headers: this.getCommonHeaders(),
        });
    }

    sendEmailToClient(scan: Scan, files: [ScanFile?]) {
        let filesToSend: any = [];

        if (files && files.length > 0) {
            filesToSend = files.map((file) => file!.name);
        }

        return this.http.post(`${BASE_URL}/sendEmail`, {
            scanId: scan.ID,
            fileNames: filesToSend
        }, {
            headers: this.getCommonHeaders(),
        });
    }

    changeStatus(id: string, statusId: string) {
        return this.http.put(`${BASE_URL}/scan/${id}`, {
            statusId: statusId
        }, {
            headers: this.getCommonHeaders(),
        });
    }

    uploadFileToRhinoServer(formData: FormData) {
        return this.http.post('http://localhost:8000/uploadfile', formData);
    }

    newScan(data: any): Observable<NewScanResponse> {
        return this.http.post<NewScanResponse>(`${BASE_URL}/scan`, data);
    }

    getRecord(orderId: string): Observable<Scan> {
        return this.http.get<Scan>(`${BASE_URL_SECOND}/usageRecord?orderId=${orderId}`, {
            headers: this.getCommonHeaders()
        }).pipe(
            map((resp: Scan) => resp)
        );
    }

    createOrder(formData: any) {
        return this.http.post(`${BASE_URL_SECOND}/order`, formData, { headers: this.getCommonHeaders() }).pipe(
            map((resp) => resp)
        );
    }

    getOrders(): Observable<OrderList> {
        return this.http.get<OrderList>(`${BASE_URL_SECOND}/orders`, { headers: this.getCommonHeaders() });
    }

    getMoreOrders(lastOrderId: string, createdAt: Date, id: string, role: string) {
        if (role === 'ROLE_3D_PRINTING_HUB') {
            return this.http.get<OrderList>(`${BASE_URL_SECOND}/orders?ID=${lastOrderId}&createdAt=${createdAt}&printingUserId=${id}`,
                { headers: this.getCommonHeaders() });
        }
        return this.http.get<OrderList>(`${BASE_URL_SECOND}/orders?ID=${lastOrderId}&createdAt=${createdAt}&userId=${id}`,
            { headers: this.getCommonHeaders() });

    }

    approveOrder(orderId: string): Observable<boolean> {
        return this.http.put<boolean>(`${BASE_URL_SECOND}/order/${orderId}/approve`, { headers: this.getCommonHeaders() });
    }

    payOrder(request: PayOrderRequest): Observable<boolean> {
        return this.http.put<boolean>(`${BASE_URL_SECOND}/order/${request.orderId}/pay`, request, { headers: this.getCommonHeaders() });
    }

    changeOrderStatus(request: any): Observable<boolean> {
        const changeStatusRequest = {
            status: request.status
        }
        return this.http.put<boolean>(`${BASE_URL_SECOND}/order/${request.orderId}/changeStatus`, changeStatusRequest,
            { headers: this.getCommonHeaders() });
    }

    updateSensingtexPatientId(scanId: string, sensingtexPatientId: string) {
        return this.http.put(`${BASE_URL}/scan/${scanId}/sensingtexPatientId`, {
            sensingtexPatientId: sensingtexPatientId
        }, {
            headers: this.getCommonHeaders(),
        });
    }

    updatePatientData(scanId: string, data: any) {
        return this.http.put(`${BASE_URL}/scan/${scanId}/patientData`, { data }, {
            headers: this.getCommonHeaders(),
        });
    }

    updateScan(scanId: string, data: any) {
        return this.http.put(`${BASE_URL}/scan/${scanId}/update`, data, {
            headers: this.getCommonHeaders(),
        });
    }

    deleteScan(scanId: string): Observable<any> {
        return this.http.delete(`${BASE_URL}/scan/${scanId}`, {
            headers: this.getCommonHeaders()
        }).pipe(
            map((resp: any) => resp)
        );
    }
}
